import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { VButton, VFlex, VModal } from '@/package';
import { useBoxClipboardStore, useToolMenuStore } from '@/store';

import { EditLinkedTrackIDPanel } from './EditLinkedTrackIDPanel';
import type { LinkedFrameItemData } from './type';
import { useEditLinkedTrackIDShared } from './useEditLinkedTrackIDShared';

export const EditLinkedTrackIDModal = () => {
  const { t } = useTranslation();
  const copiedTrackID = useBoxClipboardStore(
    state => state.copiedTrackID ?? '',
  );
  const toolMap = useToolMenuStore(state => state.toolMap);
  const navigate = useNavigate();
  const {
    boxList,
    currentFrame,
    excludedFrameSeqSet,
    formState,
    getValues,
    handleApply: handleApplyByShared,
    handleRemoveItem,
    handleRemoveList,
    hasNotAssignedFrame,
    hide,
    linkedFrameListGroup,
    register,
    show,
    totalFrameCount,
    visible,
  } = useEditLinkedTrackIDShared({
    copiedTrackID,
  });

  const handleApply = (frameList?: LinkedFrameItemData[]) => {
    return handleApplyByShared(frameList).then(hide);
  };

  const handleApplyAll = () => {
    return Promise.all(linkedFrameListGroup.map(handleApply));
  };

  const handleNavigateToFrame = (frameItem: LinkedFrameItemData) => {
    navigate(
      `/labeling/${currentFrame?.projectID}/${frameItem.taskID}/${frameItem.frameSeq}`,
    );
    hide();
  };

  useEffect(() => {
    if (!useToolMenuStore.getState().isSelectedByToolName('updateTrackID')) {
      return;
    }

    show();
  }, [toolMap]);

  return (
    <VModal
      footer={() => {
        return (
          <VFlex items="center" justify="end">
            <VFlex gap={0.25}>
              <VButton outlined size="md" variant="secondary" onClick={hide}>
                {t('common.cancel.isocinchonine')}
              </VButton>
              <VButton
                disabled={!formState.isValid}
                size="md"
                variant="primary"
                onClick={handleApplyAll}
              >
                {t(
                  'labeling.editLinkedTrackIDModalApplyAllButtonText.repercussiveness',
                )}
              </VButton>
            </VFlex>
          </VFlex>
        );
      }}
      size="small"
      visible={visible}
      onUpdateVisible={isVisible => (isVisible ? show() : hide())}
    >
      <EditLinkedTrackIDPanel
        boxList={boxList}
        excludedFrameSeqSet={excludedFrameSeqSet}
        formState={formState}
        getValues={getValues}
        hasNotAssignedFrame={hasNotAssignedFrame}
        linkedFrameListGroup={linkedFrameListGroup}
        panelTitle={t('labeling.editLinkedTrackIDModalEditTitle.graptolite')}
        register={register}
        totalFrameCount={totalFrameCount}
        onApply={handleApply}
        onClose={hide}
        onNavigate={handleNavigateToFrame}
        onRemoveItem={handleRemoveItem}
        onRemoveList={handleRemoveList}
      />
    </VModal>
  );
};

EditLinkedTrackIDModal.displayName = 'EditLinkedTrackIDModal';
